import { useContext, useMemo } from 'react';
import { RegistryContext } from './context';
import type { Area, Device, Entity, Floor, RegistryContextValue } from './types';
import { buildRegistryMaps, sortFloors } from './utils';

export function useRegistry(): RegistryContextValue {
  const ctx = useContext(RegistryContext);
  if (!ctx) {
    throw new Error('useRegistry must be used within a RegistryProvider');
  }
  return ctx;
}

export function useFloors(): Floor[] {
  const { state } = useRegistry();
  return useMemo(() => sortFloors(state.floors), [state.floors]);
}

export function useAreasByFloor(floorId: string): Area[] {
  const { state } = useRegistry();
  return useMemo(
    () => state.areas.filter((area) => area.floor_id === floorId),
    [state.areas, floorId],
  );
}

export function useEntitiesByArea(areaId: string): Entity[] {
  const { state } = useRegistry();
  return useMemo(() => {
    const maps = buildRegistryMaps(state);
    return maps.entitiesByArea.get(areaId) ?? [];
  }, [state, areaId]);
}

export function useEntitiesByDevice(deviceId: string): Entity[] {
  const { state } = useRegistry();
  return useMemo(() => {
    const maps = buildRegistryMaps(state);
    return maps.entitiesByDevice.get(deviceId) ?? [];
  }, [state, deviceId]);
}

export function useDevicesByArea(areaId: string): Device[] {
  const { state } = useRegistry();
  return useMemo(
    () => state.devices.filter((device) => device.area_id === areaId),
    [state.devices, areaId],
  );
}

export function useLabels(): string[] {
  const { state } = useRegistry();
  return state.labels;
}

export function useEntitiesByLabel(label: string): Entity[] {
  const { state } = useRegistry();
  return useMemo(
    () => state.entities.filter((entity) => entity.labels.includes(label)),
    [state.entities, label],
  );
}
